import React from 'react';
import { useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from 'contexts/ThemeContext';
import NotificationDropdown from 'components/Notifications/NotificationDropdown';
import { navLabel } from './navConfig';

/**
 * The top bar on phones and tablets.
 *
 * There was none. Layout.tsx rendered Header inside `hidden lg:block`, so below
 * 1024px a user had no notifications, no theme toggle and no page title.
 *
 * Deliberately thinner than the desktop header: title and two controls, no
 * subtitle and no name/role block. Those are in the drawer's account card, one
 * tap away, and repeating them here would cost the title its room. The
 * organisation switcher lives at the top of the drawer rather than here — it
 * governs what the whole menu below it shows, and putting it in this bar would
 * spend vertical space on every page of a device that has least of it.
 */
const MobileHeader: React.FC = () => {
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();

  // Named by the nav, so a page added to the nav is titled without touching
  // this file. Unknown routes keep the old fallback rather than showing blank.
  const title = navLabel(location.pathname) ?? 'Dashboard';

  return (
    <header
      className="lg:hidden flex-shrink-0"
      style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}
    >
      <div className="flex items-center gap-2 px-4 h-14">
        <h1
          className="text-[17px] font-semibold tracking-tight truncate flex-1 min-w-0"
          style={{ color: 'var(--text-primary)' }}
        >
          {title}
        </h1>

        <div className="flex items-center gap-1 flex-shrink-0">
          <NotificationDropdown />
          <button
            onClick={toggleTheme}
            className="tl-icon-btn"
            title={resolvedTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {resolvedTheme === 'dark'
              ? <Sun className="w-[18px] h-[18px]" />
              : <Moon className="w-[18px] h-[18px]" />}
          </button>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
